import React, { useCallback, useEffect, useRef, useState } from "react";
import { Check, Copy, KeyRound, Loader2, TriangleAlert } from "lucide-react";
import {
  DEVELOPER_KEY_CONTACT,
  DeveloperKeyInfo,
  DeveloperKeyLimitError,
  createDeveloperKey,
  listDeveloperKeys,
  revokeDeveloperKey,
} from "../utils/advancedSettings";

interface DeveloperKeysProps {
  /** Returns a fresh login token for the signed-in user. */
  getToken: () => Promise<string>;
}

export const MCP_URL = "https://mcp.deafassistant.com/mcp";
/** The one command that connects Claude Code to the hosted MCP server with this key. */
export const claudeCodeCommand = (key: string) => `claude mcp add --transport http confcap ${MCP_URL} --header "Authorization: Bearer ${key}"`;

/** The value Claude Desktop's custom connector wants in its Authorization request header. */
export const claudeDesktopHeaderValue = (key: string) => `Bearer ${key}`;

type Dialog = null | "explain" | "created" | "limit" | { revoke: DeveloperKeyInfo };

const formatTime = (iso: string) =>
  new Date(iso.endsWith("Z") ? iso : iso + "Z").toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const Modal: React.FC<{ title: string; onClose?: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" role="presentation">
      <div ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title} className="bg-white rounded-3xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 outline-none">
        <h3 className="text-xl font-bold text-slate-900">{title}</h3>
        {children}
      </div>
    </div>
  );
};

/**
 * Developer API keys: read-only keys for scripts and AI assistants. The key is shown once, so the create flow explains that
 * first and the result dialog cannot be closed by accident. Two keys per account; the limit message points to support.
 */
const DeveloperKeys: React.FC<DeveloperKeysProps> = ({ getToken }) => {
  const [keys, setKeys] = useState<DeveloperKeyInfo[] | null>(null);
  const [limit, setLimit] = useState(2);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState(false);
  const [copiedHeader, setCopiedHeader] = useState(false);
  const [savedIt, setSavedIt] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await listDeveloperKeys(getToken);
      setKeys(result.keys);
      setLimit(result.limit);
      setError(null);
    } catch (err) {
      console.error("Could not load developer keys:", err);
      setError("Could not load your developer API keys. Try again in a moment.");
    }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  const onAddClick = () => {
    if (keys && keys.length >= limit) { setDialog("limit"); return; }
    setUnderstood(false);
    setDialog("explain");
  };

  const onCreate = async () => {
    setBusy(true);
    try {
      const created = await createDeveloperKey(getToken);
      setNewKey(created.key);
      setCopied(false);
      setSavedIt(false);
      setDialog("created");
      load();
    } catch (err) {
      if (err instanceof DeveloperKeyLimitError) { setDialog("limit"); load(); }
      else { console.error("Could not create a developer key:", err); setDialog(null); setError("Could not create the key. Try again in a moment."); }
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (info: DeveloperKeyInfo) => {
    setBusy(true);
    try {
      await revokeDeveloperKey(getToken, info.id);
      setDialog(null);
      await load();
    } catch (err) {
      console.error("Could not delete the developer key:", err);
      setDialog(null);
      setError("Could not delete the key. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const copyKey = async () => {
    if (!newKey) return;
    try {
      await navigator.clipboard.writeText(newKey);
      setCopied(true);
    } catch {
      window.prompt("Copy your developer API key:", newKey);
      setCopied(true);
    }
  };

  const copyCommand = async () => {
    if (!newKey) return;
    const command = claudeCodeCommand(newKey);
    try {
      await navigator.clipboard.writeText(command);
    } catch {
      window.prompt("Copy this command:", command);
    }
    setCopiedCommand(true);
  };

  const copyHeader = async () => {
    if (!newKey) return;
    const value = claudeDesktopHeaderValue(newKey);
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      window.prompt("Copy this header value:", value);
    }
    setCopiedHeader(true);
  };

  const closeCreated = () => { setNewKey(null); setCopiedCommand(false); setCopiedHeader(false); setDialog(null); };

  return (
    <section className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100" aria-labelledby="devkeys-heading">
      <h2 id="devkeys-heading" className="text-xl font-bold text-slate-900">Developer API keys</h2>
      <p className="text-slate-600 text-sm mt-1">
        A key lets a script or an AI assistant read your stream's captions, your saved transcripts and your audience questions, without your login.
        Keys are read-only. You can have up to {limit}.
      </p>

      {error && <p className="text-red-700 text-sm mt-3" role="alert">{error}</p>}

      <div className="mt-4">
        {keys === null && !error && <Loader2 className="w-6 h-6 text-blue-600 animate-spin" aria-label="Loading" />}
        {keys && keys.length === 0 && <p className="text-slate-600 text-sm">You have no developer API keys.</p>}
        {keys && keys.length > 0 && (
          <ul className="divide-y divide-slate-100 border border-slate-100 rounded-2xl">
            {keys.map((k) => (
              <li key={k.id} className="flex items-center justify-between gap-4 p-4">
                <div className="min-w-0">
                  <p className="font-mono font-bold text-slate-900">{k.prefix}…</p>
                  <p className="text-slate-600 text-sm">Created {formatTime(k.createdAt)}</p>
                  <p className="text-slate-600 text-sm">{k.lastUsedAt ? `Last used ${formatTime(k.lastUsedAt)}` : "Never used"}</p>
                </div>
                <button onClick={() => setDialog({ revoke: k })} className="shrink-0 text-red-700 font-bold border border-red-200 rounded-xl px-4 py-2 hover:bg-red-50" aria-label={`Delete key ${k.prefix}`}>
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-4">
        <button onClick={onAddClick} disabled={keys === null} className="btn-primary inline-flex items-center gap-2">
          <KeyRound className="w-4 h-4" aria-hidden="true" /> Create Developer API Key
        </button>
      </div>

      {dialog === "explain" && (
        <Modal title="Create a developer API key" onClose={() => setDialog(null)}>
          <div className="mt-3 space-y-3 text-slate-700 text-sm">
            <p><strong>You will see this key only once.</strong> After you create it, copy it and store it somewhere safe. We keep only a scrambled version, so we cannot show it to you again. If you lose it, delete it and create another.</p>
            <p><strong>What it can do:</strong> read your stream's live captions (even when “Require authentication” is on), your saved transcripts and your audience questions. It cannot change or delete anything, and it cannot create other keys.</p>
            <p><strong>Keep it secret.</strong> Anyone who has the key can read that information. Never put it in code you share, and never push it to GitHub or any other repository. Keep it in a password manager or in your tool's secret settings. If it is ever exposed, delete it here right away.</p>
            <p>The key shows when it was last used, so you can tell if it is still in use.</p>
          </div>
          <label className="flex items-start gap-3 mt-4 text-sm text-slate-900">
            <input type="checkbox" className="mt-0.5 h-5 w-5 accent-blue-600" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />
            <span>I understand I must copy the key now, keep it secret, and not put it in a repository.</span>
          </label>
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={() => setDialog(null)} className="px-4 py-2 rounded-xl border border-slate-300 font-bold text-slate-700">Cancel</button>
            <button onClick={onCreate} disabled={!understood || busy} className="btn-primary">{busy ? "Creating..." : "Create key"}</button>
          </div>
        </Modal>
      )}

      {dialog === "created" && newKey && (
        <Modal title="Your developer API key">
          <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-2xl p-3 text-amber-900 text-sm">
            <TriangleAlert className="w-5 h-5 shrink-0" aria-hidden="true" />
            <p><strong>Copy it now.</strong> You will not be able to see this key again. Do not share it or push it to a repository.</p>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <input readOnly aria-label="Developer API key" value={newKey} onFocus={(e) => e.currentTarget.select()} className="flex-1 min-w-0 font-mono text-sm bg-slate-50 border border-slate-300 rounded-xl py-2 px-3" />
            <button onClick={copyKey} className="btn-primary inline-flex items-center gap-2 shrink-0">
              {copied ? <Check className="w-4 h-4" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <div className="mt-4 bg-slate-50 border border-slate-200 rounded-2xl p-3">
            <p className="text-sm font-bold text-slate-900">Use it with Claude Code</p>
            <p className="text-sm text-slate-600 mt-1">Run this once in a terminal, before you start Claude Code. It connects the Conference Captioning MCP server with this key. (If Claude Code is already open, run /mcp afterwards and reconnect.)</p>
            <div className="mt-2 flex items-start gap-2">
              <input readOnly aria-label="Claude Code command" value={claudeCodeCommand(newKey)} onFocus={(e) => e.currentTarget.select()} className="flex-1 min-w-0 font-mono text-xs bg-white border border-slate-300 rounded-xl py-2 px-3" />
              <button onClick={copyCommand} className="btn-primary inline-flex items-center gap-2 shrink-0">
                {copiedCommand ? <Check className="w-4 h-4" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
                {copiedCommand ? "Copied" : "Copy command"}
              </button>
            </div>
            <p className="text-sm font-bold text-slate-900 mt-4">Use it with Claude Desktop</p>
            <ol className="list-decimal pl-5 text-sm text-slate-600 mt-1 space-y-1">
              <li>Open Settings, then Connectors, then Add, then Add custom connector.</li>
              <li>Name: <span className="font-mono">ConferenceCaptioning</span>. URL: <span className="font-mono break-all">{MCP_URL}</span>. Continue (it will say no sign-in is needed).</li>
              <li>Open <strong>Request headers</strong>, choose <span className="font-mono">Authorization</span>, and paste this as the value, then Add:</li>
            </ol>
            <div className="mt-2 flex items-start gap-2">
              <input readOnly aria-label="Claude Desktop header value" value={claudeDesktopHeaderValue(newKey)} onFocus={(e) => e.currentTarget.select()} className="flex-1 min-w-0 font-mono text-xs bg-white border border-slate-300 rounded-xl py-2 px-3" />
              <button onClick={copyHeader} className="btn-primary inline-flex items-center gap-2 shrink-0">
                {copiedHeader ? <Check className="w-4 h-4" aria-hidden="true" /> : <Copy className="w-4 h-4" aria-hidden="true" />}
                {copiedHeader ? "Copied" : "Copy value"}
              </button>
            </div>
            <p className="text-xs text-slate-600 mt-2">Other assistants: add the server <span className="font-mono">{MCP_URL}</span> with the header <span className="font-mono">Authorization: Bearer your key</span>.</p>
          </div>
          <label className="flex items-start gap-3 mt-4 text-sm text-slate-900">
            <input type="checkbox" className="mt-0.5 h-5 w-5 accent-blue-600" checked={savedIt} onChange={(e) => setSavedIt(e.target.checked)} />
            <span>I have saved this key somewhere safe.</span>
          </label>
          <div className="flex justify-end mt-5">
            <button onClick={closeCreated} disabled={!savedIt} className="btn-primary">Done</button>
          </div>
        </Modal>
      )}

      {dialog === "limit" && (
        <Modal title="Key limit reached" onClose={() => setDialog(null)}>
          <p className="mt-3 text-slate-700 text-sm">
            You can have {limit} developer API keys. Delete one first, or contact us at{" "}
            <a className="text-blue-700 underline" href={`mailto:${DEVELOPER_KEY_CONTACT}?subject=Developer%20API%20key%20limit`}>{DEVELOPER_KEY_CONTACT}</a> to increase your limit.
          </p>
          <div className="flex justify-end mt-5"><button onClick={() => setDialog(null)} className="btn-primary">OK</button></div>
        </Modal>
      )}

      {dialog && typeof dialog === "object" && "revoke" in dialog && (
        <Modal title="Delete this key?" onClose={() => setDialog(null)}>
          <p className="mt-3 text-slate-700 text-sm">
            <span className="font-mono font-bold">{dialog.revoke.prefix}…</span> will stop working immediately. Anything using it will lose access. This cannot be undone.
          </p>
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={() => setDialog(null)} className="px-4 py-2 rounded-xl border border-slate-300 font-bold text-slate-700">Cancel</button>
            <button onClick={() => onRevoke(dialog.revoke)} disabled={busy} className="px-4 py-2 rounded-xl bg-red-700 text-white font-bold">{busy ? "Deleting..." : "Delete key"}</button>
          </div>
        </Modal>
      )}
    </section>
  );
};

export default DeveloperKeys;
